import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Smartphone, Globe, Code, Zap, CheckCircle2, TrendingUp, Users, HelpCircle, ChevronDown } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: Smartphone,
    value: '50+',
    label: 'Apps Deployed',
    description: 'Successful web and mobile applications launched'
  },
  {
    icon: Users,
    value: '4.8⭐',
    label: 'Average Rating',
    description: 'High user satisfaction across app stores'
  },
  {
    icon: Zap,
    value: '95%',
    label: 'On-Time Delivery',
    description: 'Projects delivered within agreed timelines'
  },
  {
    icon: CheckCircle2,
    value: '100K+',
    label: 'Active Users',
    description: 'Combined user base across deployed applications'
  }
];

const whatWeBuild = [
  {
    title: 'Mobile Applications',
    icon: Smartphone,
    items: [
      'Native iOS Development: Swift-based applications optimized for Apple ecosystem',
      'Native Android Development: Kotlin applications with Material Design principles',
      'Cross-Platform Apps: React Native and Flutter for cost-effective multi-platform deployment',
      'Mobile-First Design: Responsive interfaces optimized for touch interactions'
    ]
  },
  {
    title: 'Web Applications',
    icon: Globe,
    items: [
      'Progressive Web Apps (PWA): Offline-capable web applications with native-like features',
      'Single Page Applications: React, Angular, and Vue.js based dynamic interfaces',
      'Enterprise Web Platforms: Scalable solutions for complex business workflows',
      'E-commerce Solutions: Custom online stores with payment gateway integrations'
    ]
  },
  {
    title: 'Backend & APIs',
    icon: Code,
    items: [
      'RESTful API Development: Secure and scalable backend services',
      'Real-time Features: WebSocket implementations for live data updates',
      'Database Architecture: SQL and NoSQL solutions optimized for performance',
      'Cloud Deployment: AWS, Azure, and GCP hosting with auto-scaling'
    ]
  }
];

const techStack = [
  {
    category: 'Frontend Technologies',
    items: [
      { name: 'React / React Native', logo: '/tech-logos/react.svg' },
      { name: 'Flutter', logo: '/tech-logos/flutter.svg' },
      { name: 'TypeScript', logo: '/tech-logos/typescript.svg' }
    ]
  },
  {
    category: 'Mobile Platforms',
    items: [
      { name: 'iOS (Swift)', logo: '/tech-logos/swift.svg' },
      { name: 'Android (Kotlin)', logo: '/tech-logos/kotlin.svg' },
      { name: 'Node.js', logo: '/tech-logos/nodejs.svg' }
    ]
  },
  {
    category: 'Backend & Cloud',
    items: [
      { name: 'Python', logo: '/tech-logos/python.svg' },
      { name: 'AWS', logo: '/tech-logos/aws.svg' },
      { name: 'Firebase', logo: '/tech-logos/firebase.svg' }
    ]
  }
];

const industries = [
  {
    title: 'Healthcare',
    icon: Smartphone,
    applications: [
      'Patient portals',
      'Telemedicine apps',
      'Health tracking'
    ]
  },
  {
    title: 'Retail & E-commerce',
    icon: Globe,
    applications: [
      'Shopping applications',
      'Inventory management',
      'Loyalty programs'
    ]
  },
  {
    title: 'Fintech',
    icon: Code,
    applications: [
      'Mobile banking',
      'Payment gateways',
      'Investment platforms'
    ]
  },
  {
    title: 'Logistics',
    icon: Zap,
    applications: [
      'Delivery tracking',
      'Route optimization',
      'Fleet management'
    ]
  }
];

const methodology = [
  {
    title: 'Discovery & Planning',
    description: 'Requirements gathering and technical feasibility assessment',
    icon: Users
  },
  {
    title: 'UI/UX Design',
    description: 'User-centered design with interactive prototypes',
    icon: Smartphone
  },
  {
    title: 'Development',
    description: 'Agile sprints with continuous client feedback',
    icon: Code
  },
  {
    title: 'Testing & QA',
    description: 'Comprehensive testing across devices and platforms',
    icon: CheckCircle2
  },
  {
    title: 'Deployment',
    description: 'App store submissions and production deployment',
    icon: Globe
  },
  {
    title: 'Support & Maintenance',
    description: 'Ongoing updates, bug fixes, and feature enhancements',
    icon: TrendingUp
  }
];

const whyChooseUs = [
  {
    title: 'Modern Tech Stack',
    description: 'Latest frameworks and best practices for scalable applications',
    icon: Code
  },
  {
    title: 'User-Centric Design',
    description: 'Intuitive interfaces backed by UX research and testing',
    icon: Users
  },
  {
    title: 'Cross-Platform Expertise',
    description: 'Native and hybrid development for maximum reach',
    icon: Smartphone
  },
  {
    title: 'Agile Delivery',
    description: 'Iterative development with regular client collaboration',
    icon: Zap
  }
];

const faqs = [
  {
    question: 'How long does web/mobile app implementation take?',
    answer: 'Our rapid implementation approach delivers web and mobile applications in 6-12 weeks depending on complexity. Simple web applications: 6-8 weeks. Mobile apps (single platform): 8-10 weeks. Cross-platform apps: 10-12 weeks. We\'re 60-80% faster than traditional agencies through agile methodologies and lean teams.'
  },
  {
    question: 'What makes Agility\'s implementation approach different?',
    answer: 'We deploy senior developers directly on your project no layers of account managers or junior staff. Our agile squads ship production-ready features every week, not months, with continuous delivery and real-time collaboration. Every sprint you see working software, not presentations.'
  },
  {
    question: 'Do you provide support after deployment?',
    answer: 'Yes, we provide 24/7 post-implementation support including app store updates, bug fixes, performance monitoring, and feature enhancements. Our support packages ensure your applications continue delivering value with proactive updates and issue resolution.'
  },
  {
    question: 'What technologies do you use for development?',
    answer: 'For mobile: React Native and Flutter for cross-platform, Swift for iOS, Kotlin for Android. For web: React, TypeScript, Next.js for frontend; Node.js and Python for backend. For infrastructure: AWS, Azure, GCP with containerized deployments and CI/CD pipelines.'
  }
];

const WebMobileDevelopment = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  return (
    <>
      <SEOHead
        title="Web & Mobile App Development Services | Agility"
        description="Custom web and mobile app development. React, React Native, Node.js, cloud-native solutions. 50+ apps deployed successfully."
        keywords="web development, mobile app development, React development, React Native, full-stack development, custom software"
        url="https://www.agilitytech.ai/solutions/web-mobile-development"
      />
      <div className="min-h-screen bg-black text-white pt-24">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">
              Web & Mobile Development
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Custom web and mobile applications built with modern frameworks, delivering seamless user experiences across all platforms.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-semibold transition-colors"
            >
              Start Your Project
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {keyMetrics.map((metric, index) => {
              const Icon = metric.icon;
              return (
                <div key={index} className="text-center p-6 bg-gray-900 rounded-xl border border-gray-800">
                  <Icon className="h-10 w-10 text-teal-400 mx-auto mb-4" />
                  <div className="text-4xl font-bold text-teal-400 mb-2">{metric.value}</div>
                  <div className="text-lg font-semibold text-white mb-2">{metric.label}</div>
                  <div className="text-sm text-gray-400">{metric.description}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Build */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">What We Build</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {whatWeBuild.map((category, index) => {
              const Icon = category.icon;
              return (
                <div key={index} className="bg-black p-8 rounded-xl border border-gray-800">
                  <Icon className="h-12 w-12 text-teal-400 mb-6" />
                  <h3 className="text-2xl font-bold mb-6">{category.title}</h3>
                  <ul className="space-y-4">
                    {category.items.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 text-teal-400 mr-3 mt-1 flex-shrink-0" />
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Technology Stack</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {techStack.map((stack, index) => (
              <div key={index} className="bg-gray-900 p-6 rounded-xl border border-gray-800">
                <h3 className="text-xl font-bold mb-6 text-teal-400">{stack.category}</h3>
                <div className="space-y-4">
                  {stack.items.map((item, idx) => {
                    return (
                      <div key={idx} className="flex items-center">
                        <img
                          src={item.logo}
                          alt={`${item.name} logo`}
                          className="h-8 w-8 object-contain mr-3"
                        />
                        <span className="text-gray-300">{item.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Industries We Serve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {industries.map((industry, index) => {
              const Icon = industry.icon;
              return (
                <div key={index} className="bg-black p-6 rounded-xl border border-gray-800">
                  <Icon className="h-10 w-10 text-teal-400 mb-4" />
                  <h3 className="text-xl font-bold mb-4">{industry.title}</h3>
                  <ul className="space-y-2">
                    {industry.applications.map((app, idx) => (
                      <li key={idx} className="text-gray-400 text-sm flex items-center">
                        <span className="w-1.5 h-1.5 bg-teal-400 rounded-full mr-2"></span>
                        {app}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Our Development Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {methodology.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="bg-gray-900 p-6 rounded-xl border border-gray-800">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold mr-4">
                      {index + 1}
                    </div>
                    <Icon className="h-8 w-8 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-gray-400">{step.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <div key={index} className="text-center p-6">
                  <Icon className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">{reason.title}</h3>
                  <p className="text-gray-400">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about our web and mobile development process
              </p>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                  >
                    <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                    <ChevronDown
                      className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                        openFaqIndex === index ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaqIndex === index && (
                    <div className="px-6 pb-5">
                      <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-900 to-cyan-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Build Your Application?</h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Let's discuss your project requirements and create a solution that drives real business value.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-teal-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default WebMobileDevelopment;
